import React, { useRef } from "react";
import { FaMagnifyingGlass as FaSearch, FaArrowTrendUp } from "react-icons/fa6";
import { FaBookmark } from "react-icons/fa";
import { formatAmountForCopy } from "../utils/currency";
import { parseSpotifyTrackId, type SongRequest } from "../hooks/useSongRequest";
import ForYouShelves from "./ForYouShelves";
import type { Track } from "../types/spotify";
import "./SongSearchFeed.css";
import { formatLocale, t as tr } from "../i18n";
import { T } from "../i18n/T";

// Finding the song: the "Request a Song: $X" heading, the selected-song pill,
// the search box (typed or a pasted Spotify link) with its results overlay,
// and the Trending feed with the save bookmarks. Split out of
// pages/RequestPage.tsx; the state lives in hooks/useSongRequest.

const truncate = (s: string, n: number) =>
  (s?.length || 0) > n ? s.slice(0, n - 1) + "…" : s;

type Props = {
  displayDj: any;
  isProfilePreview: boolean;
  isFree: boolean;
  minPrice: number;
  djMinBase: number;
  tipMode: boolean;
  buyMode: boolean;
  setShowOfflineBlock: (v: boolean) => void;
  song: SongRequest;
  /** The message icon beside the "Selected Song" badge — free requests
      (founder, 1.4.1) and paid ones (2026-10-23: the message goes with the
      request, and beside Place your bid it read as part of the bid).
      null = the talent turned messages off. */
  message?: { enabled: boolean; toggle: () => void } | null;
};

export default function SongSearchFeed({ displayDj, isProfilePreview, isFree, minPrice, djMinBase, tipMode, buyMode, setShowOfflineBlock, song, message }: Props) {
  const {
    savedIds,
    feed,
    feedCountry,
    loadingFeed,
    searchQuery,
    setSearchQuery,
    results,
    setResults,
    searchError,
    selectedTrack,
    setSelectedTrack,
    setBaseAmount,
    setPricingMode,
    resultsRef,
    searchInputRef,
    riseSuppressedRef,
    feedRef,
    suppressFeedTapRef,
    setJustSelected,
    linkHint,
    setLinkHint,
    flashLinkHint,
    toggleSaved,
    resolveSpotifyLink,
    clearSelectedSong,
  } = song;
  // A song tapped in Trending or on a For-you shelf: the same pick.
  const pickTrack = (track: Track) => {
    if (displayDj?.isAcceptingRequests === false) { setShowOfflineBlock(true); return; }
    setSelectedTrack(track);
    setSearchQuery(track.name);
    setBaseAmount(String(minPrice));
    setPricingMode("custom");
  };
  // "France", "Frankreich"… — the chart's country in the app's language.
  const countryName = (() => {
    if (!feedCountry) return "";
    try {
      return new Intl.DisplayNames([formatLocale() || "en"], { type: "region" }).of(feedCountry.toUpperCase()) || "";
    } catch {
      return "";
    }
  })();
  const titleRef = useRef<HTMLDivElement | null>(null);
  const roomRef = useRef<HTMLDivElement | null>(null);
  const settleRef = useRef<(() => void) | null>(null);
  /** The Spotify move (founder round 3, re-swept 1.4.1): the fan's tap on
      the search box scrolls the page so "Request a Song: $X" and the box
      sit right under the fixed header. Pinning the page to the very top
      can't work — the keyboard needs the input above it and Safari shoves
      the page until it is — so one deliberate scroll puts the row exactly
      where Safari wants it and there is nothing left to fight. The title's
      scroll-margin (SongSearchFeed.css) is the gap under the header.
      Fires on focus AND on click: page entry focuses the box for the fan,
      so their first tap lands on an already-focused input and fires no
      focus event at all. Our own focus() sets riseSuppressedRef.
      Two rules measured in the simulator (1.4.1): the page is short (the
      Trending list is its own scroller), so the row can only reach the
      header if the page has that much scroll room — the spacer under the
      feed grows until it does (re-measured, because the page's bottom
      slack swallows the first growth); and the scroll must stay out of
      the last keyboard-inset's worth of range, because with the keyboard
      up iOS keeps the layout viewport full-height and serves that tail by
      PANNING the visual viewport, which drags the fixed header under the
      status-bar clock. So: reach = page − viewport − inset, room until
      target ≤ reach, scrollTo clamped to reach (scrollIntoView clamps
      nothing). */
  const rise = () => {
    if (isProfilePreview || riseSuppressedRef.current) return;
    const toTop = () => {
      const title = titleRef.current;
      if (!title) return;
      const gap = parseFloat(getComputedStyle(title).scrollMarginTop) || 68;
      const target = Math.max(0, Math.round(window.scrollY + title.getBoundingClientRect().top - gap));
      const doc = document.documentElement;
      const vv = window.visualViewport;
      const inset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      const reach = () => doc.scrollHeight - window.innerHeight - inset;
      // The room comes from SONGS first (founder: no dead space between the
      // last row and the keyboard): the Trending list, normally capped at
      // 360px, grows until it fills down to the keyboard, as far as it has
      // rows; only what songs can't fill goes to the empty spacer.
      const room = roomRef.current;
      const feed = feedRef.current;
      for (let pass = 0; room && pass < 6; pass++) {
        const missing = target - reach();
        if (missing <= 0) break;
        const songs = feed ? Math.min(missing, feed.scrollHeight - feed.clientHeight) : 0;
        if (feed && songs > 0) {
          feed.style.maxHeight = `${feed.clientHeight + songs}px`;
          continue;
        }
        room.style.height = `${(parseFloat(room.style.height) || 0) + missing}px`;
      }
      window.scrollTo(0, Math.max(0, Math.min(target, reach())));
    };
    requestAnimationFrame(toTop);
    // Keyboard settle differs per platform — Safari PANS the visual
    // viewport, Chrome/Android RESIZES it — so re-seat once when the
    // viewport actually changes, guarded on the input still being focused
    // (a scroll-dismissed keyboard must not yank the fan back up). One
    // shot, then gone; a second rise replaces a pending one.
    settleRef.current?.();
    const vv = window.visualViewport;
    if (!vv) return;
    const onSettle = () => {
      stop();
      if (document.activeElement === searchInputRef.current) toTop();
    };
    const timer = setTimeout(() => stop(), 900);
    const stop = () => {
      vv.removeEventListener("resize", onSettle);
      clearTimeout(timer);
      settleRef.current = null;
    };
    vv.addEventListener("resize", onSettle);
    settleRef.current = stop;
  };
  return (
    <>
      {!tipMode && !buyMode && (
        <div
          ref={titleRef}
          className="rq-section-title"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            className="rq-spotify-mark"
            aria-label={tr("songSearchFeed.poweredBySpotify")}
            title={tr("songSearchFeed.poweredBySpotify")}
          >
            <svg width="21" height="21" viewBox="0 0 168 168" aria-hidden="true">
              <circle cx="84" cy="84" r="84" fill="#1DB954" />
              <path
                fill="#fff"
                d="M122.4 121.1a5.2 5.2 0 0 1-7.2 1.7c-19.7-12-44.4-14.7-73.6-8.1a5.2 5.2 0 1 1-2.3-10.2c31.9-7.3 59.3-4.2 81.3 9.3a5.2 5.2 0 0 1 1.8 7.3zm10.2-22.8c-2.3 3.8-7.3 5-11.1 2.7-22.5-13.8-56.8-17.8-83.5-9.8-4.3 1.3-8.8-1.1-10.1-5.4-1.3-4.3 1.1-8.8 5.4-10.1 30.4-9.2 68.2-4.8 94.1 11.1 3.8 2.3 5 7.3 2.7 11.1l.5.4zm.9-23.7C106.5 58.6 62 57.1 36.2 64.9c-5.1 1.6-10.5-1.3-12.1-6.5-1.6-5.1 1.3-10.5 6.5-12.1 29.6-9 78.8-7.2 109.8 11.2 4.6 2.7 6.1 8.7 3.4 13.3a9.6 9.6 0 0 1-13.3 3.4z"
              />
            </svg>
          </span>
          {/* Price lives in the heading (founder r3) — same
              per-currency formatter as everywhere, so "$5", "50 kr",
              "2,000 Ft" all render right; free mode reads "Free".
              Founder 8/26: parens dropped, colon moved to close the GREEN
              label, amount white — so this heading and the playlist banner
              are one shape: "Green label: white number". Change both
              together or they stop matching. */}
          <span>
            {tr("songSearchFeed.requestASong")}
            <span className="rq-section-price">
              {isFree ? tr("songSearchFeed.free") : formatAmountForCopy(djMinBase, displayDj.currency)}
            </span>
          </span>
        </div>
      )}
      {!tipMode && !buyMode && selectedTrack && (
        <div className="rq-selected-pill rq-selected-pill--new">
          <img src={selectedTrack.image} alt={selectedTrack.name} loading="lazy" />
          <div className="rq-pill-text" style={{ flex: 1, minWidth: 0 }}>
            <div className="rq-pill-line1">
              {selectedTrack.artists.map((a) => a.name).join(", ")}
            </div>
            <div className="rq-pill-line2">{truncate(selectedTrack.name, 28)}</div>
            <div className="rq-pill-badge-row">
              <div className="rq-pill-badge">
                <T k="songSearchFeed.selectedSong" tags={{ 0: (c) => <span className="rq-pill-badge-check" aria-hidden>{c}</span> }} />
              </div>
              {message && (
                <button
                  type="button"
                  className={`rq-tier-info-btn rq-bid-info-btn rq-bid-msg-btn rq-pill-msg-btn${message.enabled ? " on" : ""}`}
                  onClick={message.toggle}
                  aria-label={tr("songSearchFeed.addAMessage")}
                  aria-expanded={message.enabled}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 11.5c0 4.1-4 7.5-9 7.5-1 0-2-.13-2.9-.38L4 20l1.2-3.6C3.8 15.1 3 13.4 3 11.5 3 7.4 7 4 12 4s9 3.4 9 7.5Z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {/* × back in the card's corner (founder, 1.4.1): deselects the
              song. The page's top-left arrow does the same. */}
          <button type="button" className="rq-pill-x" onClick={clearSelectedSong} aria-label={tr("songSearchFeed.removeSong")}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12" /><path d="M18 6L6 18" /></svg>
          </button>
        </div>
      )}

      {!tipMode && !buyMode && !selectedTrack && (
        <div className="rq-search-wrap">
          <FaSearch className="rq-search-ico" />
          <input
            ref={searchInputRef}
            type="search"
            placeholder={tr("songSearchFeed.searchASongOr")}
            value={searchQuery}
            onFocus={() => {
              if (displayDj?.isAcceptingRequests === false) {
                setShowOfflineBlock(true);
                searchInputRef.current?.blur();
                return;
              }
              rise();
            }}
            onClick={() => {
              if (displayDj?.isAcceptingRequests === false) return;
              rise();
            }}
            onPaste={(e) => {
              if (displayDj?.isAcceptingRequests === false) {
                setShowOfflineBlock(true);
                return;
              }
              const pasted = e.clipboardData.getData("text");
              const id = parseSpotifyTrackId(pasted);
              if (id) {
                e.preventDefault();
                setLinkHint(null);
                resolveSpotifyLink(id);
                return;
              }
              // If it smells like a non-Spotify music link, tell the user
              // politely rather than letting it become a garbage search.
              if (/^https?:\/\/|^spotify:|music\.|youtu\.?be|soundcloud\./i.test(pasted.trim())) {
                e.preventDefault();
                flashLinkHint(
                  tr("songSearchFeed.onlySpotifyLinksWork")
                );
              }
            }}
            onChange={(e) => {
              if (displayDj?.isAcceptingRequests === false) {
                setShowOfflineBlock(true);
                return;
              }
              const val = e.target.value;
              const pastedId = parseSpotifyTrackId(val);
              if (pastedId) {
                // Caller pasted a link (or typed one) — resolve it directly
                // instead of running a search for the raw URL.
                setLinkHint(null);
                resolveSpotifyLink(pastedId);
                return;
              }
              if (linkHint) setLinkHint(null);
              setSearchQuery(val);
              setSelectedTrack(null);
            }}
            className="rq-search-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="search"
            enterKeyHint="search"
            role="searchbox"
          />
          {searchQuery && (
            // Clearing keeps the keyboard up and the caret in the box
            // (Spotify): the × never takes focus, and the click re-asserts
            // the box's focus inside the fan's own gesture for Safari.
            <button
              className="rq-clear"
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => {
                setSearchQuery("");
                searchInputRef.current?.focus({ preventScroll: true });
              }}
              aria-label={tr("songSearchFeed.clear")}
            >
              ×
            </button>
          )}

          {linkHint && (
            <div className="rq-link-hint" role="alert">
              {linkHint}
            </div>
          )}

          {searchError && (
            <div className="rq-search-error" role="alert">
              {searchError}
            </div>
          )}

          {results.length > 0 && (
            <ul className="rq-results" ref={resultsRef}>
              {results.map((track) => (
                <li
                  key={track.id}
                  onClick={() => {
                    if (displayDj?.isAcceptingRequests === false) { setShowOfflineBlock(true); return; }
                    setSelectedTrack(track);
                    setSearchQuery(track.name);
                    setResults([]);
                    setJustSelected(true);
                    setBaseAmount(String(minPrice));
                    setPricingMode("custom");
                  }}
                >
                  <img src={track.image} alt={track.name} loading="lazy" />
                  <div className="rq-res-text">
                    <div className="rq-res-title">{track.name}</div>
                    <div className="rq-res-sub">{track.artists.map((a) => a.name).join(", ")}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Trending stays rendered behind the results overlay (founder
          8/24 round 2, Spotify convention): the panel floats OVER the
          content — never unmount what's behind it. */}
      {!tipMode && !buyMode && !selectedTrack && (
        <>
          {!isProfilePreview && <ForYouShelves djSlug={displayDj?.slug} djName={displayDj?.name} onPick={pickTrack} />}
          <div className="rq-feed-heading">
            <FaArrowTrendUp size={16} aria-hidden />
            <span>{tr("songSearchFeed.trending")}</span>
            {countryName && <span className="fy-country">· {countryName}</span>}
          </div>

          <div className="rq-feed" ref={feedRef}>
            {loadingFeed && <div className="rq-feed-skel">{tr("songSearchFeed.loading")}</div>}
            {!loadingFeed &&
              feed.map((t) => (
                <div
                  key={t.id}
                  className="rq-feed-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (suppressFeedTapRef.current) { suppressFeedTapRef.current = false; return; }
                    pickTrack(t);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      (e.currentTarget as HTMLDivElement).click();
                    }
                  }}
                >
                  <div className="rq-feed-left">
                    <img src={t.image} alt={t.name} loading="lazy" />
                    <div className="rq-feed-text">
                      <div className="rq-feed-title">{t.name}</div>
                      <div className="rq-feed-sub">
                        {t.artists.map((a) => a.name).join(", ")}
                      </div>
                    </div>
                  </div>

                  <button
                    className={`rq-feed-save ${savedIds.includes(t.id) ? "on" : ""}`}
                    title={savedIds.includes(t.id) ? tr("songSearchFeed.removeFromSaved") : tr("songSearchFeed.save")}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSaved(t.id);
                    }}
                  >
                    <FaBookmark />
                  </button>
                </div>
              ))}
            {!loadingFeed && feed.length === 0 && (
              <div className="rq-feed-empty">{tr("songSearchFeed.noSongsHereYet")}</div>
            )}
          </div>
          {/* Scroll room for the rise (see `rise`); 0 until the fan taps. */}
          <div className="rq-search-room" ref={roomRef} aria-hidden="true" />
        </>
      )}
    </>
  );
}
